import { useState, useEffect } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import Animated from 'react-native-reanimated'
import { router } from 'expo-router'
import { FlashList } from '@shopify/flash-list'

import { trpc } from 'api'
import cn from '@ui/utils/cn'
import { useToast } from '@ui/toasts/useToastManager'
import { useCustomer } from 'hooks/useSession'
import useFlattenInfiniteData from 'hooks/useFlattenInfiniteData'
import { useRefreshByUser } from 'hooks/useRefreshByUser'
import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import AnimatedEmpty from '@ui/animated/AnimatedEmpty'
import EmptyResult from '@ui/components/EmptyResult'
import { Collapsible } from '@ui/animated/Collapsible'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { LoadingFooter } from '@ui/lists/LoadingFooter'
import useStartOrder from '@order/hooks/useStartOrder'
import { useActiveDeal } from '@order/hooks/useDeal'
import OrderHistoryItem from '@order/OrderHistory/Item'
import {
  OrderStatusGroup,
  defaultOrderStatusGroup,
  orderStatusesByGroup,
} from '@ca/common/types'
import StatusGroups from './StatusGroups'

const REQUEST_LIMIT = 10

export default function CustomerOrderHistory() {
  const toast = useToast()
  const { t } = useTranslation(['common', 'ui', 'order', 'toasts'])
  const text = 'order:history'
  const textEmpty = 'order:history.empty'

  const customer = useCustomer()
  const utils = trpc.useUtils()
  const startOrder = useStartOrder()
  const deal = useActiveDeal()

  const [group, setGroup] = useState<OrderStatusGroup>(defaultOrderStatusGroup)
  const states = orderStatusesByGroup[group]
  const hasFilters = states && states.length > 0

  const handleGroupChange = (group: OrderStatusGroup) => {
    setGroup(group)
  }

  const {
    data,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isInitialLoading,
    isRefetching,
    isFetching,
    isFetched,
    error,
  } = trpc.order.history.useInfiniteQuery(
    {
      limit: REQUEST_LIMIT,
      customer_id: customer?.id?.toString() || 0,
      states,
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.meta.nextCursor
      },
      placeholderData: (v) => v,
      refetchOnWindowFocus: false,
      enabled: !!customer?.id,
    }
  )

  useEffect(() => {
    if (!error) {
      return
    }
    if (error.data?.code === 'UNAUTHORIZED') {
      return
    }
    toast({
      variant: 'error',
      title: t('toasts:error.order.history.title'),
      text: t('toasts:error.order.history.desc'),
      duration: 5 * 60 * 1000,
    })
  }, [error, t, toast])

  const handleRefresh = () => {
    utils.order.history.setInfiniteData(
      {
        limit: REQUEST_LIMIT,
        customer_id: customer?.id?.toString() || 0,
      },
      (data) => ({
        pages: data?.pages.slice(0, 1) ?? [],
        pageParams: data?.pageParams.slice(0, 1) ?? [],
      })
    )

    return refetch()
  }

  const { isRefetchingByUser, refetchByUser } = useRefreshByUser(
    handleRefresh,
    isRefetching
  )

  const isLoadings =
    (isFetching || isInitialLoading || isLoading) && !isFetchingNextPage

  const orders = useFlattenInfiniteData(data)

  const showLoading = isLoadings
  const showEmpty = !isLoadings && (isFetched || orders.length === 0)
  const showItems = isFetched && !isLoadings

  const styleLoading = useAnimatedOpacity(showLoading)
  const styleEmpty = useAnimatedOpacity(showEmpty)
  const styleItems = useAnimatedOpacity(showItems)

  return (
    <>
      <Page stage="customer" list>
        <FlashList
          ListHeaderComponent={
            <>
              <Header
                list
                title={t(`${text}.header.title`)}
                desc={t(`${text}.header.desc`)}
              />
              <View className="px-8">
                <View className="flex-row justify-center">
                  <StatusGroups
                    selected={group}
                    setSelected={handleGroupChange}
                  />
                </View>
              </View>
              <Collapsible open={showLoading} fullWidth>
                <LoadingFooter style={styleLoading} />
              </Collapsible>
            </>
          }
          ListFooterComponent={isFetchingNextPage ? LoadingFooter : null}
          data={orders}
          numColumns={1}
          keyboardShouldPersistTaps="handled"
          contentContainerClassName="pb-8"
          onEndReachedThreshold={0.25}
          onRefresh={refetchByUser}
          refreshing={isRefetchingByUser}
          progressViewOffset={100}
          // extraData={customerFrameIds}
          onEndReached={hasNextPage ? fetchNextPage : undefined}
          renderItem={({ item }) => {
            return (
              <Animated.View style={styleItems} className={cn('px-8 pt-8')}>
                <OrderHistoryItem
                  data={item}
                  onPress={() =>
                    router.navigate(`/customer/order/details/${item.id}`)
                  }
                />
              </Animated.View>
            )
          }}
          ListEmptyComponent={
            <AnimatedEmpty style={styleEmpty}>
              <EmptyResult
                title={t(
                  `${textEmpty}.${hasFilters ? 'filter' : 'result'}.title`
                )}
                desc={t(
                  `${textEmpty}.${hasFilters ? 'filter' : 'result'}.desc`
                )}
                button={
                  hasFilters
                    ? undefined
                    : {
                        label: t('ui:newOrder'),
                        variant: 'outlined',
                        iconEnd: 'plus',
                        onPress: () => startOrder(deal?.settings?.flow),
                      }
                }
              />
            </AnimatedEmpty>
          }
        />
      </Page>
    </>
  )
}
